import { useState } from "react";
import { X, ArrowUpRight } from "lucide-react";
import { METHODOLOGY_PHASES } from "@/content/damp-survey";
import { ResponsiveImage } from "@/components/responsive-image";
import { getRasterImage } from "@/generated/raster-images";
import type { RasterImageSet } from "@/generated/raster-images";

interface ServiceCard {
  title: string;
  bullets: string[];
  modalKey: string;
}
interface ServiceCategory {
  title: string;
  plainLabel: string;
  shortDesc: string;
  image: string;
  cards: ServiceCard[];
}
interface ModalSection {
  heading: string;
  content: string;
}
interface ServiceModal {
  title: string;
  sections: ModalSection[];
}

const CATEGORY_IMAGES: Record<string, RasterImageSet> = {
  survey1: getRasterImage("damp-survey/services/structural-penetrating-damp"),
  survey2: getRasterImage("damp-survey/services/external-repairs-management"),
  survey3: getRasterImage("damp-survey/services/breathable-wall-moisture-control"),
  survey4: getRasterImage("damp-survey/services/cavity-insulation-assessment"),
  survey5: getRasterImage("damp-survey/services/controlled-ventilation-air-quality"),
};

const PHASE_DESCRIPTIONS: Record<number, string> = {
  1: "Gathering key property details, age, and symptom patterns before the visit.",
  2: "Evidence-led, on-site investigation analysing building fabric, moisture gradients, and ventilation.",
  3: "Translating verified findings into a proportionate, costed action plan.",
  4: "Implementing agreed measures using compatible materials for your building's era.",
  5: "Post-works validation, long-term monitoring advice, and warranty documentation.",
};

const PHASE_TITLES: Record<number, string> = {
  1: "Discovery And History",
  2: "Forensic Assessment",
  3: "Consultative Advice",
  4: "Targeted Remediation",
  5: "Verified Results",
};

const CATEGORIES: ServiceCategory[] = [
  {
    title: "Structural Integrity And Penetrating Damp Defence",
    plainLabel: "When Water Is Getting In Through The Building Fabric",
    shortDesc:
      "For damp that may be entering through damaged brickwork, failed pointing, wall tie issues or hidden debris bridging moisture inside the cavity.",
    image: "survey1",
    cards: [
      { title: "Structural Repairs And Wall Ties", bullets: [], modalKey: "A" },
      { title: "Cavity Clearance And Rubble Removal", bullets: [], modalKey: "B" },
    ],
  },
  {
    title: "External Repairs And Water Management",
    plainLabel: "Fixing Outside Defects That Let Moisture In",
    shortDesc:
      "For damp linked to gutters, downpipes, drainage, roof junctions, render, brickwork or ground levels around the property.",
    image: "survey2",
    cards: [
      { title: "External Fabric And Drainage Repairs", bullets: [], modalKey: "E" },
      { title: "DPC And Ground-Level Correction", bullets: [], modalKey: "F" },
    ],
  },
  {
    title: "Breathable Wall And Moisture Control Systems",
    plainLabel: "Helping Walls Manage Moisture Properly",
    shortDesc:
      "For older, solid or sensitive walls where breathability, salts, plaster condition or cold internal surfaces may be contributing to damp.",
    image: "survey3",
    cards: [
      { title: "Lime Repointing And Masonry Repairs", bullets: [], modalKey: "I" },
      { title: "Internal Moisture Control Finishes", bullets: [], modalKey: "J" },
    ],
  },
  {
    title: "Cavity Insulation Assessment And Upgrade",
    plainLabel: "Dealing With Failed, Wet Or Unsuitable Cavity Insulation",
    shortDesc:
      "For homes where existing insulation may be saturated, poorly installed, unsuitable or contributing to damp and cold wall problems.",
    image: "survey4",
    cards: [
      { title: "Insulation Assessment And Extraction", bullets: [], modalKey: "C" },
      { title: "Insulation Renewal And Upgrade", bullets: [], modalKey: "D" },
    ],
  },
  {
    title: "Controlled Ventilation And Air Quality",
    plainLabel: "Reducing Condensation, Humidity And Stale Air",
    shortDesc:
      "For mould, condensation, high humidity, poor airflow or recurring moisture build-up inside the home.",
    image: "survey5",
    cards: [
      { title: "PIV (Positive Input Ventilation)", bullets: [], modalKey: "G" },
      { title: "Intelligent Extraction And Trickle Vents", bullets: [], modalKey: "H" },
    ],
  },
];

const COMMON_PROBLEMS = [
  "Damp Patches After Rain",
  "Black Mould Around Windows",
  "Condensation That Keeps Returning",
  "Cold External Walls",
  "Musty Smells",
  "Failed Or Wet Cavity Insulation",
  "Cracked Render Or Pointing",
  "Poor Ventilation",
];

const SERVICE_MODALS: Record<string, ServiceModal> = {
  A: {
    title: "Structural Repairs And Wall Ties",
    sections: [
      { heading: "In Plain English", content: "This is about checking whether water is getting into the property from outside, or whether the wall structure itself needs attention." },
      { heading: "Why This Matters", content: "If moisture is entering through brickwork, pointing, render, roof junctions or wall tie issues, internal treatments alone will not solve the problem. The route of moisture needs to be understood before the right repair can be recommended." },
      { heading: "What We Check", content: "• The defects identified in your assessment and how they relate to symptom locations\n• Repair priority order (what must be resolved first)\n• Whether wall tie investigation/remediation is indicated based on risk and evidence\n• Access, scope, and constraints that affect repair approach" },
      { heading: "What We May Do", content: "• Carry out targeted external repairs (e.g., pointing/render repairs, local masonry repairs, sealing of details) as specified\n• Where required, progress wall tie investigation and remedial work in line with the agreed scope\n• Document what was done so follow-on reinstatement is based on known changes" },
      { heading: "Good To Know", content: "Not every damp issue needs this type of work. We only recommend it where the evidence suggests the building fabric is contributing to the problem." },
    ],
  },
  B: {
    title: "Cavity Clearance And Rubble Removal",
    sections: [
      { heading: "In Plain English", content: "We check whether debris or mortar inside the wall cavity is letting moisture cross from the outside leaf to the inside, then clear it." },
      { heading: "Why This Matters", content: "Debris and bridging inside a cavity can allow moisture to transfer across the wall, leading to localised damp. Clearing the obstruction helps the cavity perform as intended." },
      { heading: "What We Check", content: "• The assessed likelihood of bridging and the affected wall areas\n• Access points required and the limitations of clearance\n• Whether other contributors (defective pointing, gutters, trays) must be corrected alongside\n• The condition and suitability of the cavity post-clearance" },
      { heading: "What We May Do", content: "• Open inspection points at strategic locations to confirm bridging\n• Remove mortar droppings, rubble, and other obstructions from the cavity\n• Reinstate and seal access points to maintain weather resistance\n• Document findings and coordinate any related pointing or gutter repairs" },
      { heading: "Good To Know", content: "Clearance is only recommended where evidence of bridging is present. We do not open cavities speculatively." },
    ],
  },
  C: {
    title: "Insulation Assessment And Extraction",
    sections: [
      { heading: "In Plain English", content: "If the insulation inside your cavity walls has failed or got wet, it may be making damp worse. This is about checking it and removing it where needed." },
      { heading: "Why This Matters", content: "Cavity wall insulation is beneficial when installed correctly and the home is suitable. However, older or outdated insulation may have failed, trapping moisture and increasing damp risk. Extraction is the necessary first step on the pathway to upgrading." },
      { heading: "What We Check", content: "• Evidence of insulation saturation or failure from the assessment findings\n• Which areas and elevations are affected\n• Whether partial or full extraction is appropriate\n• The upgrade pathway: what product and approach would be suitable after extraction" },
      { heading: "What We May Do", content: "• Carry out controlled extraction of failed or saturated insulation material\n• Clear the cavity to restore its drainage and ventilation function\n• Reinstate and seal all access points\n• Provide post-extraction guidance including drying timelines, monitoring, and upgrade recommendations" },
      { heading: "Good To Know", content: "Extraction is only recommended where insulation is shown to be contributing to the damp issue, not as a default first step." },
    ],
  },
  D: {
    title: "Insulation Renewal And Upgrade",
    sections: [
      { heading: "In Plain English", content: "Once a failed cavity has been cleared, we look at replacing it with insulation that suits your home and won't repeat the problem." },
      { heading: "Why This Matters", content: "If your original insulation has failed, upgrading to a more appropriate product can resolve the issues caused by the previous material while still improving your home's thermal performance." },
      { heading: "What We Check", content: "• Construction type, exposure zone, and cavity condition post-extraction\n• Breathability requirements and moisture-risk factors\n• Existing ventilation pathways and whether they need adjustment\n• Suitability of the proposed upgrade product for your specific property" },
      { heading: "What We May Do", content: "• Install the specified insulation product in accordance with manufacturer and regulatory requirements\n• Ensure ventilation pathways are maintained or adjusted as needed\n• Coordinate with any other remedial works to avoid conflict\n• Provide confirmation documentation, aftercare guidance, and warranty information" },
      { heading: "Good To Know", content: "Upgrading is only recommended where the property and construction type are suitable. Some homes are better left uninsulated at cavity level." },
    ],
  },
  E: {
    title: "External Fabric And Drainage Repairs",
    sections: [
      { heading: "In Plain English", content: "Many damp issues come from the outside of the building. This is about fixing things like brickwork, render, gutters and drainage so water is kept away from your walls." },
      { heading: "Why This Matters", content: "Many damp issues stem from defects in the building's exterior or poor water control. Resolving these first may mean that other costly internal repairs are not required." },
      { heading: "What We Check", content: "• Brickwork, render and mortar condition relative to symptom locations\n• Gutter, downpipe and drainage performance\n• Flashing and roof junction integrity\n• Whether external defects are the primary moisture source before considering internal works" },
      { heading: "What We May Do", content: "• Repair brickwork, render and mortar joints to restore weather protection\n• Reseal openings and address damaged flashings and roof junctions\n• Improve gutters, downpipes and drainage to direct rainwater safely away from the property\n• Document repairs to inform any follow-on internal reinstatement" },
      { heading: "Good To Know", content: "Tackling external defects first often resolves damp without the need for any internal works." },
    ],
  },
  F: {
    title: "DPC And Ground-Level Correction",
    sections: [
      { heading: "In Plain English", content: "If damp is rising at low level, this is about checking whether ground levels outside, or a missing damp-proof course, is the cause." },
      { heading: "Why This Matters", content: "Installing a new damp-proof barrier can stop upward moisture movement, but where raised ground levels are bridging the DPC, correction of the external levels may be all that's needed." },
      { heading: "What We Check", content: "• Moisture profile and distribution pattern at low level\n• Whether raised ground levels are bridging the existing DPC\n• Internal and external bridging risks\n• Whether a chemical or physical DPC barrier is appropriate, or if ground-level adjustment alone will resolve the issue" },
      { heading: "What We May Do", content: "• Where appropriate, assess and lower or adjust ground levels to reinstate effective DPC protection\n• Install a new chemical or physical DPC barrier where rising damp is confirmed and correction alone is insufficient\n• Address identified bridging points (internal and external) where within scope\n• Document the works for guarantee and future reference" },
      { heading: "Good To Know", content: "Chemical DPC injection is only recommended when evidence clearly supports it, not by default." },
    ],
  },
  G: {
    title: "PIV (Positive Input Ventilation)",
    sections: [
      { heading: "In Plain English", content: "A small loft-mounted unit that gently replaces stale, damp air in your home with fresher, filtered air to reduce condensation and mould." },
      { heading: "Why This Matters", content: "Where condensation risk is driven by humidity load and limited airflow pathways, PIV can help dilute moisture-laden air and support more consistent internal humidity." },
      { heading: "What We Check", content: "• Humidity load and occupancy-driven moisture production\n• Airflow pathways, loft space suitability, and installation constraints\n• Whether PIV alone is sufficient or needs to complement other measures\n• Commissioning settings appropriate for the property's characteristics" },
      { heading: "What We May Do", content: "• Install and commission a PIV unit in the recommended location (typically loft-mounted)\n• Set airflow and filtration settings based on the property profile\n• Provide user guidance on operation, maintenance, and filter replacement\n• Coordinate with any other ventilation or remediation measures in the plan" },
      { heading: "Good To Know", content: "PIV is most effective when the property has reasonable airtightness and clear airflow pathways." },
    ],
  },
  H: {
    title: "Intelligent Extraction And Trickle Vents",
    sections: [
      { heading: "In Plain English", content: "Better fans in kitchens and bathrooms, plus small background vents, to remove moisture at source and stop humidity building up." },
      { heading: "Why This Matters", content: "Effective local extraction in wet rooms and suitable background ventilation help remove moisture at source and reduce humidity build-up that contributes to mould and condensation." },
      { heading: "What We Check", content: "• Extract flow rates in kitchens, bathrooms, and utility areas\n• Whether existing fans are performing to required standards\n• Trickle vent provision and background ventilation adequacy\n• Ducting routes, noise constraints, and practical installation considerations" },
      { heading: "What We May Do", content: "• Install or upgrade extraction fans with humidity-sensing automatic controls where specified\n• Fit or replace trickle vents to improve background ventilation\n• Ensure ducting is correctly routed and terminated externally\n• Commission and test all units to confirm adequate airflow rates" },
      { heading: "Good To Know", content: "Many condensation problems are resolved by upgrading ventilation alone, without the need for further works." },
    ],
  },
  I: {
    title: "Lime Repointing And Masonry Repairs",
    sections: [
      { heading: "In Plain English", content: "Older and stone-built homes need a softer, breathable mortar. This is about repairing pointing and masonry with materials that work with your wall type." },
      { heading: "Why This Matters", content: "In solid-wall, stone or older properties, moisture must be managed, not trapped. Using incompatible materials like cement on a lime-built wall can seal moisture in, accelerating decay and worsening damp." },
      { heading: "What We Check", content: "• Whether the existing mortar and masonry are lime-based or cement-based\n• The extent of failed pointing and its relationship to moisture ingress\n• Whether water-repellent treatments are appropriate while maintaining vapour permeability\n• The building's character and any conservation considerations" },
      { heading: "What We May Do", content: "• Carry out lime-compatible repointing to restore breathability and weather resistance\n• Apply specialist water-repellent treatments where appropriate, preserving vapour escape\n• Repair damaged masonry while maintaining building character\n• Document works to support ongoing maintenance and any follow-on internal finishes" },
      { heading: "Good To Know", content: "Cement repairs on a lime-built wall can make damp worse. Material compatibility matters as much as workmanship." },
    ],
  },
  J: {
    title: "Internal Moisture Control Finishes",
    sections: [
      { heading: "In Plain English", content: "Replacing damaged plaster with breathable, salt-resistant finishes and, where helpful, thin insulation to keep wall surfaces warmer." },
      { heading: "Why This Matters", content: "Internally, damaged plaster can harbour salts and moisture. Replacing it with breathable or salt-resistant finishes can improve surface temperatures and reduce condensation risk." },
      { heading: "What We Check", content: "• The condition of existing internal plaster and whether it's contributing to ongoing symptoms\n• Whether salt contamination is present and requires a resistant finish\n• Surface temperature patterns and condensation risk areas\n• Whether thin insulation systems are appropriate for the wall type and room use" },
      { heading: "What We May Do", content: "• Replace damaged plaster with breathable or salt-resistant plaster systems as specified\n• Where appropriate, install thin moisture-control insulation to improve surface temperatures\n• Ensure all finishes are compatible with the wall construction and any external works carried out\n• Provide guidance on maintaining the correct internal environment to support long-term performance" },
      { heading: "Good To Know", content: "Internal finishes only work long-term when the underlying moisture cause has been addressed first." },
    ],
  },
};

function scrollToHeroForm() {
  if (typeof window === "undefined") return;
  document.getElementById("damp-survey-hero-form")?.scrollIntoView({
    behavior: "smooth",
    block: "start",
  });
}

export function DiagnosisRemediationSection() {
  const [modalKey, setModalKey] = useState<string | null>(null);
  const modal = modalKey ? SERVICE_MODALS[modalKey] ?? null : null;

  return (
    <section
      id="methodology"
      className="py-16 md:py-24 bg-secondary relative overflow-hidden"
    >
      <div className="container-page relative z-10">
        {/* Header */}
        <div className="text-center mb-12 md:mb-16 max-w-3xl mx-auto">
          <h2 className="text-2xl md:text-4xl font-bold text-foreground mb-4">
            Comprehensive Diagnosis. Targeted Remediation.
          </h2>
          <p className="text-muted-foreground leading-relaxed">
            One accountable team takes you from finding the true cause to fixing it properly, every recommendation driven by evidence and matched to what your property actually needs.
          </p>
        </div>

        {/* Methodology timeline */}
        <div className="mb-16 md:mb-20">
          <h3 className="text-lg md:text-2xl font-bold text-primary mb-10 md:mb-12 tracking-wide text-center">
            Our 5-Phase Methodology
          </h3>

          {/* Desktop horizontal timeline */}
          <div className="hidden md:block relative max-w-6xl mx-auto">
            <div
              className="absolute top-6 left-[10%] right-[10%] h-0.5 bg-primary/20 rounded-full"
              aria-hidden="true"
            />
            <div className="relative grid grid-cols-5 gap-6">
              {METHODOLOGY_PHASES.map((phase) => (
                <div
                  key={phase.num}
                  className="flex flex-col items-center text-center px-2"
                >
                  <div className="relative z-10 w-12 h-12 rounded-full bg-primary flex items-center justify-center shadow-md mb-4">
                    <span className="text-base font-bold text-primary-foreground">
                      {phase.num}
                    </span>
                  </div>
                  <h4 className="text-sm font-bold text-foreground mb-1.5">
                    {PHASE_TITLES[phase.num]}
                  </h4>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {PHASE_DESCRIPTIONS[phase.num]}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Mobile vertical timeline */}
          <div className="md:hidden relative pl-14 max-w-md mx-auto">
            <div className="absolute left-[18px] top-2 bottom-2 w-0.5 bg-primary/20 rounded-full" />
            <div className="space-y-6">
              {METHODOLOGY_PHASES.map((phase) => (
                <div key={phase.num} className="relative">
                  <div className="absolute -left-14 top-0 w-9 h-9 rounded-full bg-primary flex items-center justify-center shadow-md">
                    <span className="text-sm font-bold text-primary-foreground">
                      {phase.num}
                    </span>
                  </div>
                  <h4 className="text-sm font-bold text-foreground mb-1">
                    {PHASE_TITLES[phase.num]}
                  </h4>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {PHASE_DESCRIPTIONS[phase.num]}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Services block */}
        <div className="pt-12 md:pt-14 border-t border-border/60">
          <h3 className="text-lg md:text-2xl font-bold text-primary mb-2 tracking-wide text-center">
            Damp And Mould Solutions, Matched To The Cause
          </h3>
          <p className="text-sm md:text-base text-muted-foreground mb-8 leading-relaxed text-center max-w-3xl mx-auto">
            Once we understand what is causing the problem, we recommend the right type of work for your home, from external repairs and ventilation improvements to insulation checks, breathable wall systems and moisture control.
          </p>

          {/* Common problems strip */}
          <div className="mb-10 max-w-4xl mx-auto text-center">
            <p className="text-[11px] md:text-xs font-semibold text-primary/80 uppercase tracking-wider mb-3">
              Common Problems We Help With
            </p>
            <div className="flex flex-wrap justify-center gap-1.5 md:gap-2">
              {COMMON_PROBLEMS.map((p) => (
                <span
                  key={p}
                  className="inline-flex items-center text-[11px] md:text-xs font-medium text-muted-foreground bg-card/60 border border-primary/15 rounded-full px-2.5 py-0.5"
                >
                  {p}
                </span>
              ))}
            </div>
            <p className="text-xs md:text-sm text-muted-foreground mt-4">
              The right solution depends on the cause. Select a service area below to see what we check and may recommend.
            </p>
          </div>

          {/* Service card grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {CATEGORIES.map((cat, i) => {
              const img = CATEGORY_IMAGES[cat.image];
              const primaryModalKey = cat.cards[0]?.modalKey;
              const isLast = i === CATEGORIES.length - 1;
              const spanFull = isLast && CATEGORIES.length % 2 === 1;
              return (
                <button
                  key={cat.title}
                  type="button"
                  onClick={() => primaryModalKey && setModalKey(primaryModalKey)}
                  aria-label={`Open details for ${cat.title}`}
                  className={`group relative text-left rounded-2xl overflow-hidden bg-card shadow-soft border border-border/50 flex flex-col transition-all duration-300 hover:shadow-lift hover:-translate-y-0.5 hover:border-primary/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 ${spanFull ? "md:col-span-2 md:max-w-3xl md:mx-auto md:w-full" : ""}`}
                >
                  {img && (
                    <div className="relative w-full aspect-[16/9] bg-secondary overflow-hidden">
                      <ResponsiveImage
                        image={img}
                        alt={cat.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                      <span className="absolute top-3 right-3 w-9 h-9 rounded-full bg-card/90 backdrop-blur flex items-center justify-center shadow-sm text-primary transition-transform duration-300 group-hover:bg-primary group-hover:text-primary-foreground group-hover:scale-110">
                        <ArrowUpRight className="h-4 w-4" />
                      </span>
                    </div>
                  )}
                  <div className="flex-1 p-5 md:p-6 flex flex-col gap-2.5">
                    <p className="text-xs md:text-[13px] text-primary font-semibold leading-snug">
                      {cat.plainLabel}
                    </p>
                    <h4 className="text-base md:text-lg font-bold text-foreground leading-tight">
                      {cat.title}
                    </h4>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {cat.shortDesc}
                    </p>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {cat.cards.map((card) => (
                        <span
                          key={card.title}
                          className="text-[11px] md:text-xs font-medium text-foreground bg-secondary border border-border rounded-full px-2.5 py-1"
                        >
                          {card.title}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center justify-end gap-1 mt-3 text-xs font-medium text-primary/80 group-hover:text-primary transition-colors">
                      <span>See Details</span>
                      <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 text-center">
          <button
            type="button"
            onClick={scrollToHeroForm}
            className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full bg-primary text-primary-foreground text-base font-bold hover:bg-primary/90 transition-colors shadow-md"
          >
            Request My Damp Survey
          </button>
        </div>
      </div>

      {/* Service detail modal */}
      {modal && (
        <div
          className="fixed inset-0 bg-foreground/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in"
          onClick={() => setModalKey(null)}
        >
          <div
            className="bg-card rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8 md:p-10 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="absolute top-6 right-6 bg-secondary rounded-full w-10 h-10 flex items-center justify-center hover:bg-muted transition-colors"
              onClick={() => setModalKey(null)}
              aria-label="Close"
            >
              <X className="h-5 w-5 text-foreground" />
            </button>
            <h2 className="text-xl font-bold text-foreground mb-6 pr-12">
              {modal.title}
            </h2>
            {modal.sections.map((s) => (
              <div key={s.heading} className="mb-5 border-b border-border pb-5">
                <h4 className="text-sm font-bold text-primary uppercase mb-2">
                  {s.heading}
                </h4>
                <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
                  {s.content}
                </p>
              </div>
            ))}
            <div className="mt-6 flex justify-center">
              <button
                type="button"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-primary text-primary-foreground text-sm font-bold hover:bg-primary/90 transition-colors"
                onClick={() => {
                  setModalKey(null);
                  scrollToHeroForm();
                }}
              >
                Check My Home
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
